import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Calendar, Users, TrendingUp, CheckCircle, Code, Zap, Shield } from 'lucide-react';

export const TechStartupPortfolio: React.FC = () => {
  const technologies = [
    'React', 'Node.js', 'MongoDB', 'Stripe API', 'AWS', 'TypeScript'
  ];

  const features = [
    'Custom React Dashboard',
    'Real-time Analytics',
    'Payment Processing',
    'User Management System',
    'API Integration',
    'Mobile Responsive Design',
    'SEO Optimization',
    'Performance Monitoring'
  ];

  const results = [
    { metric: 'User Engagement', value: '300%', description: 'Increase in user session duration' },
    { metric: 'Conversion Rate', value: '45%', description: 'Improvement in lead generation' },
    { metric: 'Page Load Speed', value: '2.1s', description: 'Average page load time' },
    { metric: 'Mobile Traffic', value: '65%', description: 'Of total website traffic' }
  ];

  return (
    <div>
      {/* Hero Section */}
      <section className="pt-32 pb-16 bg-gradient-to-br from-primary-600 to-secondary-600 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link to="/portfolio" className="inline-flex items-center text-primary-100 hover:text-white mb-8 transition-colors">
            <ArrowLeft className="h-5 w-5 mr-2" />
            Back to Portfolio
          </Link>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="flex items-center mb-4">
                <span className="px-3 py-1 bg-accent-500 text-white text-sm font-medium rounded-full">
                  Tech Startup
                </span>
              </div>
              <h1 className="text-4xl sm:text-5xl font-bold mb-6">
                InnovateTech Solutions
              </h1>
              <p className="text-xl text-primary-100 mb-8 leading-relaxed">
                A comprehensive web platform for a B2B SaaS startup featuring custom dashboards, 
                real-time analytics, and seamless payment integration that transformed their business operations.
              </p>
            </div>
            
            <div className="relative">
              <img
                src="https://images.pexels.com/photos/3184360/pexels-photo-3184360.jpeg?auto=compress&cs=tinysrgb&w=800&h=600&fit=crop"
                alt="InnovateTech Solutions Dashboard"
                className="rounded-lg shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Project Overview */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            <div className="lg:col-span-2">
              <h2 className="text-3xl font-bold text-gray-900 mb-6">Project Overview</h2>
              <div className="prose prose-lg max-w-none text-gray-600">
                <p className="mb-6">
                  InnovateTech Solutions approached us with a challenge: their existing website wasn't 
                  converting visitors into customers, and they needed a sophisticated platform to showcase 
                  their SaaS products while providing seamless user onboarding and payment processing.
                </p>
                <p className="mb-6">
                  We developed a comprehensive React-based web application that serves as both a marketing 
                  platform and a functional dashboard for their B2B clients. The solution includes real-time 
                  analytics, custom user dashboards, and integrated payment processing through Stripe API.
                </p>
                <p>
                  The result was a 300% increase in user engagement and a 45% improvement in lead generation, 
                  establishing InnovateTech as a leader in their industry.
                </p>
              </div>
            </div>
            
            <div className="space-y-8">
              <div className="card p-6">
                <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
                  <Calendar className="h-6 w-6 text-primary-600 mr-2" />
                  Project Timeline
                </h3>
                <div className="space-y-3 text-gray-600">
                  <div className="flex justify-between">
                    <span>Duration:</span>
                    <span className="font-medium">12 weeks</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Launch Date:</span>
                    <span className="font-medium">March 2024</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Team Size:</span>
                    <span className="font-medium">5 specialists</span>
                  </div>
                </div>
              </div>
              
              <div className="card p-6">
                <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
                  <Code className="h-6 w-6 text-primary-600 mr-2" />
                  Technologies Used
                </h3>
                <div className="flex flex-wrap gap-2">
                  {technologies.map((tech, index) => (
                    <span key={index} className="px-3 py-1 bg-primary-100 text-primary-700 text-sm rounded-full">
                      {tech}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Key Features */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Key Features Delivered</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Every feature was carefully designed and developed to enhance user experience and drive business results.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, index) => (
              <div key={index} className="card p-6 text-center">
                <CheckCircle className="h-8 w-8 text-accent-500 mx-auto mb-4" />
                <h3 className="font-semibold text-gray-900">{feature}</h3>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Results & Impact */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Results & Impact</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              The new platform delivered measurable improvements across all key performance indicators.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {results.map((result, index) => (
              <div key={index} className="text-center">
                <div className="text-4xl font-bold text-primary-600 mb-2">{result.value}</div>
                <div className="text-lg font-semibold text-gray-900 mb-2">{result.metric}</div>
                <div className="text-gray-600">{result.description}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Challenges & Solutions */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-12 text-center">Challenges & Solutions</h2>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <div className="card p-8">
              <div className="flex items-center mb-6">
                <Zap className="h-8 w-8 text-yellow-500 mr-3" />
                <h3 className="text-xl font-semibold text-gray-900">Challenge: Complex Data Visualization</h3>
              </div>
              <p className="text-gray-600 mb-4">
                The client needed real-time analytics dashboards that could handle large datasets while 
                maintaining fast performance and intuitive user experience.
              </p>
              <p className="text-gray-600">
                <strong>Solution:</strong> We implemented React with optimized state management and created 
                custom data visualization components that update in real-time without performance degradation.
              </p>
            </div>
            
            <div className="card p-8">
              <div className="flex items-center mb-6">
                <Shield className="h-8 w-8 text-green-500 mr-3" />
                <h3 className="text-xl font-semibold text-gray-900">Challenge: Secure Payment Processing</h3>
              </div>
              <p className="text-gray-600 mb-4">
                Integration of secure payment processing for B2B subscriptions with complex pricing tiers 
                and billing cycles.
              </p>
              <p className="text-gray-600">
                <strong>Solution:</strong> Implemented Stripe API with custom webhook handling for automated 
                billing, subscription management, and secure payment processing with PCI compliance.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Client Testimonial */}
      <section className="py-20 bg-primary-600 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <blockquote className="text-2xl font-medium mb-8">
            "OutstandingNet transformed our vision into reality. The new platform has revolutionized how we 
            interact with our clients and has directly contributed to our 300% growth in user engagement."
          </blockquote>
          <div className="flex items-center justify-center">
            <img
              src="https://images.pexels.com/photos/2379004/pexels-photo-2379004.jpeg?auto=compress&cs=tinysrgb&w=100&h=100&fit=crop"
              alt="Sarah Johnson"
              className="w-16 h-16 rounded-full mr-4"
            />
            <div className="text-left">
              <div className="font-semibold">Sarah Johnson</div>
              <div className="text-primary-200">CEO, InnovateTech Solutions</div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">
            Ready to Transform Your Business?
          </h2>
          <p className="text-xl text-gray-600 mb-8">
            Let's discuss how we can create a custom solution that drives results for your company.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/get-started" className="btn-primary text-lg px-8 py-4">
              Start Your Project
            </Link>
            <Link to="/portfolio" className="btn-outline text-lg px-8 py-4">
              View More Projects
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};